/**
 * crypto.ts — bridge to the REAL, proven Legacy Architect client crypto
 * (src/client/recipient/decrypt.ts + src/crypto/keys.ts).
 *
 * We deliberately do NOT reimplement any crypto here. The compiled client
 * modules are vendored as browser ESM under `../vendor` by
 * web/recipient/prepare-crypto.sh (tsc -p tsconfig.vendor.json). We import them
 * STATICALLY so Vite/Rollup bundles them — which resolves the `@hpke/*` bare
 * specifiers from node_modules and tree-shakes the server-only modules
 * (pii/local-kms) that never appear on the decrypt path.
 *
 * This guarantees the recipient decrypts with the exact same HPKE-x25519 +
 * AES-256-GCM path the integration tests + synthetic-family harness prove.
 * (The decrypt path uses WebCrypto throughout — no Node builtins — since
 * cek.ts derives its nonce via SubtleCrypto HKDF.)
 */

// Vendored client crypto (browser ESM under ../vendor, emitted by
// prepare-crypto.sh). Typed by inference via tsconfig `allowJs` (checkJs off).
import { decryptMessage as vendorDecryptMessage } from '../vendor/client/recipient/decrypt.js';
import { sealResponse } from '../vendor/client/recipient/response-recorder.js';
import {
  generateRecipientKeyPair,
  importPrivateKeyForRecovery,
  exportPublicKeyForServer,
} from '../vendor/crypto/keys.js';

export interface DecryptedPayload {
  blob: Blob;
  messageId: string;
  format: 'video' | 'voice' | 'letter' | 'document';
  mimeType: string;
  byteLength: number;
}

interface DecryptApi {
  fetchDecryptContext(messageId: string): Promise<unknown>;
}

/** Decrypt a delivered message on-device. `email` resolves the private key from IndexedDB. */
export async function decryptMessage(args: {
  api: DecryptApi;
  messageId: string;
  email: string;
}): Promise<DecryptedPayload> {
  return vendorDecryptMessage({
    api: args.api,
    messageId: args.messageId,
    privateKeyLookup: { email: args.email },
  });
}

/**
 * Adapter the vendored `sealResponse` needs. For letter replies only
 * `fetchResponseContext` + `postResponse` are used (no media upload).
 */
export interface ReplyApi {
  fetchResponseContext(sourceMessageId: string): Promise<{
    source_message_id: string;
    vault_owner_public_key: string;
    cek_algorithm_version: string;
  }>;
  postResponse(input: {
    sourceMessageId: string;
    format: 'video' | 'voice' | 'letter';
    cekCiphertext: string;
    ownerPublicKey: string;
    cekAlgorithmVersion: string;
    ivB64: string;
    r2Key: string | null;
    letterCiphertextB64: string | null;
  }): Promise<{ response_id: string; sealed_at: string }>;
}

/**
 * Seal a LETTER reply to the vault owner. The reply's CEK is generated on this
 * device, the text is AES-GCM-encrypted, and the CEK is HPKE-wrapped to the
 * OWNER's device key (mirror of the seal flow) — the exact vendored
 * `sealResponse` the backend tests exercise. Letter ciphertext travels inline.
 */
export async function sealLetterReply(args: {
  api: ReplyApi;
  sourceMessageId: string;
  text: string;
}): Promise<{ response_id: string }> {
  const contentBytes = new TextEncoder().encode(args.text);
  const res = await sealResponse({
    api: {
      fetchResponseContext: (id: string) => args.api.fetchResponseContext(id),
      postResponse: (input: Parameters<ReplyApi['postResponse']>[0]) => args.api.postResponse(input),
      presignResponseUpload: () => {
        throw new Error('Letter replies do not upload media.');
      },
    },
    sourceMessageId: args.sourceMessageId,
    format: 'letter',
    contentBytes,
  });
  return { response_id: res.response_id };
}

/**
 * Onboarding: generate a recipient X25519 keypair, store the private key in
 * IndexedDB (the exact store decryptMessage reads — `legacy-architect-keys` /
 * `identity-keys`, keyed by email), and return the server-serialized public key
 * to register against the recipient's account.
 */
export async function onboardGenerateKeys(email: string): Promise<{ publicKeyForServer: string }> {
  const pair = await generateRecipientKeyPair();
  const privKey: CryptoKey = await importPrivateKeyForRecovery(pair.privateKey);
  await storePrivateKey(email, privKey);
  return { publicKeyForServer: exportPublicKeyForServer(pair.publicKey) };
}

const IDB_NAME = 'legacy-architect-keys';
const IDB_STORE = 'identity-keys';

function storePrivateKey(email: string, key: CryptoKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE, { keyPath: 'email' });
    };
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put({ email, key, registeredAt: new Date().toISOString() });
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => reject(tx.error);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function hasKeyFor(email: string): Promise<boolean> {
  return new Promise((resolve) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE, { keyPath: 'email' });
    };
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction(IDB_STORE, 'readonly');
      const get = tx.objectStore(IDB_STORE).get(email);
      get.onsuccess = () => { db.close(); resolve(get.result !== undefined); };
      get.onerror = () => { db.close(); resolve(false); };
    };
    req.onerror = () => resolve(false);
  });
}
